import { computed, ref } from 'vue';
import axios from 'axios';
import {
  StatusItem,
  TrafficBaseline,
  TrafficConfig,
  TrafficMode,
  TrafficRule,
  TrafficState,
  TrafficUsage
} from '@/types';

const CONFIG_URL = 'json/traffic_config.json';
const BASELINE_URL = 'json/traffic_baseline.json';

/** 基线比当前周期起点早多久才判定为“过期”，用于容忍时区差与 cron 执行时刻 */
const STALE_TOLERANCE = 2 * 24 * 3600 * 1000;

/** 服务商 API 数据多久没刷新就提示异常（cron 每 10 分钟一次，2 小时足够宽松） */
const API_STALE_AFTER = 2 * 3600 * 1000;

const config = ref<TrafficConfig>();
const baseline = ref<TrafficBaseline>();

const KIB = 1024;
const UNITS: Record<string, number> = {
  B: 1,
  K: KIB, KB: KIB, KIB: KIB,
  M: KIB ** 2, MB: KIB ** 2, MIB: KIB ** 2,
  G: KIB ** 3, GB: KIB ** 3, GIB: KIB ** 3,
  T: KIB ** 4, TB: KIB ** 4, TIB: KIB ** 4,
  P: KIB ** 5, PB: KIB ** 5, PIB: KIB ** 5
};

/** '1TB' / '500 GB' / 1099511627776 → 字节数；无法识别时返回 0 */
export const parseSize = (value: unknown): number => {
  if (typeof value === 'number') return Number.isFinite(value) && value > 0 ? value : 0;
  if (typeof value !== 'string') return 0;
  const matched = /^\s*([\d.]+)\s*([a-zA-Z]*)\s*$/.exec(value);
  if (!matched) return 0;
  const amount = Number.parseFloat(matched[1]);
  if (!Number.isFinite(amount) || amount <= 0) return 0;
  const unit = UNITS[(matched[2] || 'B').toUpperCase()];
  return unit ? amount * unit : 0;
};

const safeNumber = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

const daysInMonth = (year: number, month: number): number => new Date(year, month + 1, 0).getDate();

/** 账单日落在当月的具体日期（2 月没有 31 号时取最后一天） */
const boundaryOf = (year: number, month: number, resetDay: number): Date =>
  new Date(year, month, Math.min(resetDay, daysInMonth(year, month)), 0, 0, 0, 0);

const normalizeResetDay = (value: unknown): number => {
  const day = Math.round(safeNumber(value)) || 1;
  return Math.min(Math.max(day, 1), 31);
};

/** 当前账单周期的起点（本地时区 00:00） */
export const cycleStartOf = (resetDay: number, now: Date = new Date()): Date => {
  const day = normalizeResetDay(resetDay);
  const thisMonth = boundaryOf(now.getFullYear(), now.getMonth(), day);
  if (now.getTime() >= thisMonth.getTime()) return thisMonth;
  return boundaryOf(now.getFullYear(), now.getMonth() - 1, day);
};

/** 当前账单周期的终点，即下一个账单日 00:00 */
export const cycleEndOf = (resetDay: number, now: Date = new Date()): Date => {
  const day = normalizeResetDay(resetDay);
  const start = cycleStartOf(day, now);
  return boundaryOf(start.getFullYear(), start.getMonth() + 1, day);
};

/** 按计费口径折算成计费流量 */
export const billedBytes = (inBytes: number, outBytes: number, mode: TrafficMode): number => {
  if (mode === 'out') return outBytes;
  if (mode === 'max') return Math.max(inBytes, outBytes);
  return inBytes + outBytes;
};

/** 合并全局默认与逐台覆盖 */
const resolveRule = (server: StatusItem) => {
  const global = config.value || {};
  const servers = global.servers || {};
  // 先按节点名找，再按 host 找；“键存在”本身就代表这台要统计，哪怕额度是 0
  const key = Object.prototype.hasOwnProperty.call(servers, server.name) ? server.name
    : Object.prototype.hasOwnProperty.call(servers, server.host) ? server.host
      : undefined;
  const perServer = key === undefined ? undefined : servers[key];
  const rule: TrafficRule = (typeof perServer === 'string' || typeof perServer === 'number')
    ? { limit: perServer }
    : (perServer || {});

  // 额度优先级：逐台配置 > 全局默认 > 探针自带的 traffic_limit 字段。
  // 用 undefined 判断而不是真值判断，这样显式写 0 才能盖掉后面的回退。
  const rawLimit = rule.limit !== undefined ? rule.limit
    : global.limit !== undefined ? global.limit
      : server.traffic_limit;
  const limit = parseSize(rawLimit);

  return {
    limit,
    // 出现在配置里就统计，即使不限额；没出现则看探针有没有给额度
    tracked: key !== undefined || limit > 0,
    resetDay: normalizeResetDay(rule.resetDay ?? global.resetDay ?? 1),
    mode: (rule.mode || global.mode || 'sum') as TrafficMode,
    warnPercent: safeNumber(rule.warnPercent ?? global.warnPercent) || 80
  };
};

const emptyUsage = (): TrafficUsage => ({
  tracked: false,
  limited: false,
  used: 0,
  limit: 0,
  percent: 0,
  rawPercent: 0,
  level: 'success',
  state: 'ok',
  source: 'probe',
  mode: 'sum',
  cycleStart: 0,
  cycleEnd: 0,
  daysLeft: 0
});

/** 补上百分比与告警等级；不限额时只带用量 */
const withPercent = (
  base: TrafficUsage, used: number, state: TrafficState, warnPercent: number
): TrafficUsage => {
  if (!base.limited) return { ...base, used, state };
  const rawPercent = Math.round((used / base.limit) * 100);
  return {
    ...base,
    used,
    state,
    rawPercent,
    percent: Math.min(100, Math.max(0, rawPercent)),
    level: rawPercent >= 100 ? 'error' : rawPercent >= warnPercent ? 'warning' : 'success'
  };
};

/** 计算单台机器的本月用量 */
export const trafficOf = (server: StatusItem, now: Date = new Date()): TrafficUsage => {
  if (!server || !server.name) return emptyUsage();

  const rule = resolveRule(server);
  const entry = baseline.value?.servers?.[server.name] ?? baseline.value?.servers?.[server.host];
  const fromApi = entry?.source === 'api';
  // API 数据自带额度，即使 traffic_config.json 没配这台也应该展示
  if (!rule.tracked && !fromApi) return { ...emptyUsage(), mode: rule.mode };

  const cycleStart = cycleStartOf(rule.resetDay, now);
  // 服务商给的重置时间比我们自己推算的账单日更权威
  const cycleEnd = fromApi && safeNumber(entry?.cycle_end)
    ? new Date(safeNumber(entry?.cycle_end) * 1000)
    : cycleEndOf(rule.resetDay, now);
  const limit = fromApi ? (parseSize(entry?.limit) || rule.limit) : rule.limit;
  const base: TrafficUsage = {
    ...emptyUsage(),
    tracked: true,
    limited: limit > 0,
    limit,
    source: fromApi ? 'api' : 'probe',
    mode: rule.mode,
    cycleStart: cycleStart.getTime(),
    cycleEnd: cycleEnd.getTime(),
    daysLeft: Math.max(0, Math.ceil((cycleEnd.getTime() - now.getTime()) / 86400000))
  };

  if (fromApi) {
    // 服务商计量与探针无关，节点离线照样准确
    const fetchedAt = safeNumber(entry?.fetched_at) * 1000;
    return withPercent(base, safeNumber(entry?.used),
      fetchedAt && now.getTime() - fetchedAt > API_STALE_AFTER ? 'api-stale' : 'ok',
      rule.warnPercent);
  }

  // 离线时探针上报的计数不可信（多为 0），不做差值
  if (!(server.online4 || server.online6)) return { ...base, state: 'offline' };

  const current = billedBytes(safeNumber(server.network_in), safeNumber(server.network_out), rule.mode);

  let used = current;
  let state: TrafficState = 'ok';

  if (!entry) {
    // 基线还没建立，只能把累计值当本月用量，偏大
    state = 'no-baseline';
  } else {
    const anchor = billedBytes(safeNumber(entry.network_in), safeNumber(entry.network_out), rule.mode);
    if (current < anchor) {
      // agent 重启导致计数归零，脚本还没来得及修正基线
      state = 'counter-reset';
      used = current;
    } else {
      used = current - anchor;
      if (safeNumber(entry.reset_at) * 1000 >= cycleStart.getTime()) {
        // 脚本已把基线清零，本周期只能统计到重启之后的部分
        state = 'counter-reset';
      } else if (safeNumber(entry.cycle_start) * 1000 < cycleStart.getTime() - STALE_TOLERANCE) {
        state = 'stale-baseline';
      }
    }
  }

  return withPercent(base, used, state, rule.warnPercent);
};

export const TRAFFIC_STATE_HINT: Record<TrafficState, string> = {
  'ok': '',
  'offline': '节点离线，暂不统计',
  'no-baseline': '基线未建立，显示累计值',
  'stale-baseline': '基线已过期，请检查 cron',
  'counter-reset': 'agent 重启过，仅统计重启后',
  'api-stale': 'API 数据未刷新，请检查 cron'
};

/** 当前节点列表，用于判断探针是否自带 traffic_limit */
const knownServers = ref<StatusItem[]>([]);

export const setTrafficServers = (servers?: Array<Partial<StatusItem>>): void => {
  knownServers.value = (servers || []) as StatusItem[];
};

/**
 * 至少有一台机器要统计时，UI 才展示月流量相关内容。
 * 直接复用逐台判定，保证表头与单元格的显示条件永远一致。
 */
const trafficEnabled = computed(() =>
  knownServers.value.some(server => trafficOf(server).tracked)
);

const fetchJson = <T>(url: string) => axios.get<T>(url)
  .then(res => (typeof res.data === 'object' && res.data !== null ? res.data : undefined))
  .catch(() => undefined); // 文件不存在时静默降级，不影响主面板

/** 拉取配置（一次）与基线（跨周期后需要刷新，由调用方定时重拉） */
export const loadTrafficConfig = () => fetchJson<TrafficConfig>(CONFIG_URL)
  .then(data => (config.value = data));

export const loadTrafficBaseline = () => fetchJson<TrafficBaseline>(BASELINE_URL)
  .then(data => (baseline.value = data));

export default () => ({
  trafficEnabled,
  trafficOf,
  loadTrafficConfig,
  loadTrafficBaseline
});
